import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment, usePaginationFragment } from 'react-relay';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { Box, Flex } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { ModalEntryPointButtonTrigger } from '@atlassian/jira-modal-entry-point-button-trigger/src/ModalEntryPointButtonTrigger.tsx';
import { FieldWrapper } from '@atlassian/jira-personal-settings-common/src/view/styled.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import {
	ContextualAnalyticsData,
	SCREEN,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { projectNotificationConfirmationEntryPoint } from '@atlassian/jira-project-notifications-modal/src/entrypoint.tsx';
import type { projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projects$key } from '@atlassian/jira-relay/src/__generated__/projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projects.graphql';
import type { projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projectsPaginatedFragment$key } from '@atlassian/jira-relay/src/__generated__/projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projectsPaginatedFragment.graphql';
import type { projectNotificationsConfig_personalSettingsNotificationsConfigTable_projects$key } from '@atlassian/jira-relay/src/__generated__/projectNotificationsConfig_personalSettingsNotificationsConfigTable_projects.graphql';
import type { ProjectNotificationsPaginationQuery } from '@atlassian/jira-relay/src/__generated__/ProjectNotificationsPaginationQuery.graphql';
import { ProjectNotificationAccordionSkeleton } from '@atlassian/jira-skeletons/src/ui/personal-settings-notifications/PersonalSettingsNotificationsAppSkeleton.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import {
	type AccordionState,
	AccordionContainer,
	useAccordions,
} from '../../common/ui/accordion/index.tsx';
import { ConnectionContext } from '../../services/project-connection/index.tsx';
import { ProjectNotificationsEmptyState } from './empty-state/index.tsx';
import messages from './messages.tsx';
import { ProjectNotificationConfigTableNew } from './project-notifications-config-table/index.tsx';

const ProjectNotificationsConfigInner = ({
	projects,
	projectsPaginatedFragment,
}: {
	projects: projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projects$key;
	projectsPaginatedFragment: projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projectsPaginatedFragment$key;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { open } = useAccordions();
	const [isWarningPopUpOpen, setIsWarningPopUpOpen] = useState(false);

	const fragment =
		useFragment<projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projects$key>(
			graphql`
				fragment projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projects on Query {
					...projectNotificationsConfigTable_personalSettingsNotificationsConfigTable_ProjectNotificationConfigTableNew_enabledPreferenceFragment
					# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
					...ui_projectNotificationsModal_ProjectNotificationsModal
				}
			`,
			projects,
		);
	const { data: paginatedProjects } = usePaginationFragment<
		ProjectNotificationsPaginationQuery,
		projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projectsPaginatedFragment$key
	>(
		graphql`
			fragment projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projectsPaginatedFragment on Query
			@argumentDefinitions(
				# todo: once the UI has pagination, the value should go back down to 10
				first: { type: "Int", defaultValue: 50 }
				after: { type: "String" }
			)
			@refetchable(queryName: "ProjectNotificationsPaginationQuery") {
				jira @required(action: THROW) {
					allNotificationProjectPreferences(cloudId: $cloudId, first: $first, after: $after)
						@required(action: THROW)
						@connection(key: "projects_allNotificationProjectPreferences")
						@optIn(to: "JiraNotificationPreference") {
						__id @required(action: THROW)
						edges @required(action: THROW) {
							node {
								...projectNotificationsConfigTable_personalSettingsNotificationsConfigTable_ProjectNotificationConfigTableNew_projectPreferencesData
								project {
									id @required(action: THROW)
								}
							}
						}
						totalCount @required(action: THROW)
					}
				}
			}
		`,
		projectsPaginatedFragment,
	);

	const notificationPreferences = paginatedProjects?.jira?.allNotificationProjectPreferences;
	const paginatedPreferences = notificationPreferences?.edges;
	const totalConfiguredProjects = notificationPreferences?.totalCount;

	const handleNewProject = useCallback(
		(projectId: string) => {
			open(projectId);
		},
		[open],
	);

	const projectNotifModalProps = {
		onClose: () => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'close',
				actionSubject: 'modalDialog',
			});
			fireUIAnalytics(analyticsEvent, 'projectNotificationConfirmationEntryPointModal');
		},
		connectionId: paginatedProjects.jira.allNotificationProjectPreferences.__id,
		onProjectAdditionSuccess: (value: string) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'submitted',
				actionSubject: 'modalDialog',
			});
			fireUIAnalytics(analyticsEvent, 'projectNotificationConfirmationEntryPointModal');

			handleNewProject(value);
		},
		jiraQueryFragment: fragment,
	};

	const onOpen = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'open',
			actionSubject: 'modalDialog',
		});
		fireUIAnalytics(analyticsEvent, 'projectNotificationConfirmationEntryPointModal');
	};

	return (
		<>
			<FieldWrapper>
				<Flex justifyContent="space-between" alignItems="center">
					<SectionHeading>
						{formatMessage(messages.notificationProjectSettingsHeading)}
					</SectionHeading>
					<Flex alignItems="center">
						{totalConfiguredProjects >= 45 && totalConfiguredProjects < 50 ? (
							<Popup
								placement="left-start"
								messageId="personal-settings-notifications-config-table.ui.project-notifications-config.popup"
								messageType="transactional"
								isOpen={isWarningPopUpOpen}
								onClose={() => setIsWarningPopUpOpen(false)}
								content={() => (
									<Box padding="space.200">
										<WarningPopUpTitle>
											{formatMessage(
												messages.notificationProjectSettingsHardLimitWarningPopUpTextTitle,
											)}
										</WarningPopUpTitle>{' '}
										{formatMessage(messages.notificationProjectSettingsHardLimitWarningPopUpText)}
									</Box>
								)}
								trigger={({ 'aria-expanded': _, ...triggerProps }) => (
									<WarningPopUpButtonContainer
										onMouseEnter={() => setIsWarningPopUpOpen(true)}
										data-testid="personal-settings-notifications-config-table.ui.project-notifications-config.warning-pop-up-button-container"
										{...triggerProps}
									>
										<WarningIcon
											spacing="spacious"
											label="warning"
											color={token('color.icon.warning')}
										/>
									</WarningPopUpButtonContainer>
								)}
							/>
						) : null}
						<ModalEntryPointButtonTrigger
							entryPoint={projectNotificationConfirmationEntryPoint}
							entryPointProps={projectNotifModalProps}
							isDisabled={totalConfiguredProjects >= 50}
							interactionName="open-add-project-notification-modal"
							testId="personal-settings-notifications-config-table.ui.project-notifications-config.button"
							onClick={onOpen}
							useInternalModal
						>
							{formatMessage(messages.notificationProjectSettingsAddProjectNotificationsButtonText)}
						</ModalEntryPointButtonTrigger>
					</Flex>
				</Flex>
				<ProjectSectionDescription>
					{formatMessage(messages.notificationProjectSettingsDescription)}
				</ProjectSectionDescription>
				{totalConfiguredProjects >= 50 ? (
					<SectionMessage
						appearance="warning"
						data-testId="personal-settings-notifications-config-table.ui.project-notifications-config.section-message"
					>
						{formatMessage(messages.notificationProjectSettingsHardLimitWarningBannerText)}
					</SectionMessage>
				) : null}
			</FieldWrapper>
			<>
				<ConnectionContext.Provider
					value={paginatedProjects.jira.allNotificationProjectPreferences.__id}
				>
					{paginatedPreferences.length ? (
						paginatedPreferences.map((edge) =>
							edge?.node?.project?.id ? (
								<ProjectNotificationConfigTableNew
									key={`${edge.node.project.id}`}
									projectPreferencesData={edge.node}
									enabledPreferenceFragment={fragment}
								/>
							) : null,
						)
					) : (
						<ProjectNotificationsEmptyState />
					)}
				</ConnectionContext.Provider>
			</>
		</>
	);
};

export const ProjectNotificationsConfig = ({
	projects,
}: {
	projects: projectNotificationsConfig_personalSettingsNotificationsConfigTable_projects$key;
}) => {
	const [projectIds] = useQueryParam('projectIds');
	const data =
		useFragment<projectNotificationsConfig_personalSettingsNotificationsConfigTable_projects$key>(
			graphql`
				fragment projectNotificationsConfig_personalSettingsNotificationsConfigTable_projects on Query {
					...projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projectsPaginatedFragment
					...projectNotificationsConfig_personalSettingsNotificationsConfigTable_ProjectNotificationsConfigInner_projects
				}
			`,
			projects,
		);
	return (
		<Placeholder
			name="project-notifications-accordion"
			fallback={<ProjectNotificationAccordionSkeleton />}
		>
			<ContextualAnalyticsData
				sourceName="personalSettingsProjectNotifications"
				sourceType={SCREEN}
			>
				<AccordionContainer
					initial={(projectIds || '').split(',').reduce((acc, id) => {
						acc[id] = {
							isOpen: true,
						};
						return acc;
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					}, {} as AccordionState)}
				>
					<ProjectNotificationsConfigInner projects={data} projectsPaginatedFragment={data} />
				</AccordionContainer>
			</ContextualAnalyticsData>
		</Placeholder>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectSectionDescription = styled.div({
	gap: token('space.200'),
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionHeading = styled.h5({
	gap: token('space.200'),
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WarningPopUpButtonContainer = styled.span({
	marginRight: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WarningPopUpTitle = styled.span({
	fontWeight: token('font.weight.bold'),
});
